import { Box } from "@mui/material"
import { FC, useCallback, useEffect, useMemo, useRef } from "react"
import { useTranslation } from "react-i18next"
import { LockerDoorReservationSessionThingTypeMrn } from "shared/lockerDoorReservationSessionData"
import { SharedAccessPatternOsEnum } from "shared/sharedAccessPatternsData"
import { useAppDispatch, useAppSelector } from "../app/hooks"
import {
  selectMe,
  selectMeIsReady,
  selectTenantId,
} from "../features/me/meSlice"
import {
  useCreateThingsMutation,
  useDeleteThingsMutation,
  useGetThingsQuery,
} from "../features/things/thingsApi"
import {
  selectSelectedDoorReservation,
  thingsActions,
} from "../features/things/thingsSlice"
import { orderedV2Modules } from "./lockerModuleOrder"
import OsLockerDoorDrawer from "./OsLockerDoorDrawer"

type OsLockerDoorDrawerManagerProps = {}

export const OsLockerDoorDrawerManager: FC<
  OsLockerDoorDrawerManagerProps
> = () => {
  const me = useAppSelector(selectMe)

  const { t } = useTranslation()

  const tenantId = useAppSelector(selectTenantId)

  const isReady = useAppSelector(selectMeIsReady)

  const [deleteThings] = useDeleteThingsMutation()

  const [createThings] = useCreateThingsMutation()

  const dispatch = useAppDispatch()

  const selectedDoorReservation = useAppSelector(selectSelectedDoorReservation)

  const { locker, session, sessions, reservation, door } =
    selectedDoorReservation

  const lockerId = locker?.id || "-1"

  useGetThingsQuery(
    {
      params: {
        "ap.name": SharedAccessPatternOsEnum.GetLockerCurrentReservations,
        "ap.tenantId": tenantId,
        "ap.lockerId": lockerId,
        "ap.limit": 2000,
        tenantId,
      },
    },
    {
      skip: !tenantId || !isReady || !lockerId || lockerId === "-1",
    },
  )

  // sessionLockerId/sessionTenantId default to the *currently* selected
  // locker/tenant (correct for onClose, which always acts on the door that's
  // open right now) but can be overridden with the values captured at the
  // time an older session was created — needed when cleaning up a session
  // that belonged to a door/locker we've since navigated away from.
  const onRemoveReservationSession = useCallback(
    async (
      sessionId: string,
      sessionLockerId: string | undefined = lockerId,
      sessionTenantId: string | undefined = tenantId,
    ) => {
      const toDelete = {
        thingTypeMrn: LockerDoorReservationSessionThingTypeMrn,
        tenantId: sessionTenantId,
        lockerId: sessionLockerId,
        id: sessionId,
      }
      await deleteThings({
        body: { things: [toDelete] },
        params: { tenantId: sessionTenantId },
      }).unwrap()
    },
    [deleteThings, lockerId, tenantId],
  )

  const previousDoor = useRef<{
    doorId?: string
    sessionId?: string
    lockerId?: string
    tenantId?: string
  }>({ doorId: door?.door_id, sessionId: session?.id, lockerId, tenantId })

  // Doors with a session create currently in flight, keyed the same way the
  // guard below is. Independent backstop to the synchronous ref write
  // (LOCKER-377): the ref alone is enough today, but it is one edit away from
  // being re-broken, and a second create is a row nobody can see and nobody
  // releases.
  const creatingDoorKeys = useRef<Set<string>>(new Set())

  useEffect(() => {
    const previous = previousDoor.current
    const doorId = door?.door_id
    // A door's identity is the LOCKER plus the SST door id, never the door id
    // alone (LOCKER-377, review F1.3). SST door ids are not known to be
    // globally unique, so keying on `door_id` by itself made locker A door "5"
    // and locker B door "5" the same door: switching between them took the
    // early return and the arriving door got no session at all — the exact
    // "two operators on one free door" hole DEV-201 introduced sessions to
    // close. Same key on `creatingDoorKeys`, or the in-flight guard would
    // reintroduce the hole on its own.
    const doorKey = `${lockerId}::${doorId ?? ""}`

    if (previous.doorId === doorId && previous.lockerId === lockerId) {
      // Same door — the effect just re-ran because a poll tick or a store
      // action replaced an object identity in the deps. Adopt the session id
      // if the polled cache has caught up with a create we already made, and
      // touch the network for nothing else.
      if (!previous.sessionId && session?.id) {
        previousDoor.current = { ...previous, sessionId: session.id }
      }
      return
    }

    // LOCKER-377 — THE FIX. The ref is written SYNCHRONOUSLY, before any
    // await. The old code wrote it only after both round trips resolved, so
    // every re-run inside that window re-read the stale `previous.doorId`,
    // passed this guard again and fired a second `createThings` — two rows
    // whose `sessionStart` differ only by the render gap, which is exactly the
    // "same instant" duplicate signature in the production data.
    previousDoor.current = {
      doorId,
      sessionId: session?.id,
      lockerId,
      tenantId,
    }

    // Create a session for the newly selected door. Version-agnostic: the
    // session keys on the SL2 lockerId + the SST door id, which
    // `selectSelectedDoorReservation` already normalizes for V2 doors
    // (DEV-201 — V2 was ported without sessions in #240, letting two
    // operators open the same free door at once).
    const shouldCreate = !!(locker && doorId && !reservation?.id)
    const canCreate = shouldCreate && !creatingDoorKeys.current.has(doorKey)
    if (canCreate) creatingDoorKeys.current.add(doorKey)

    // Captured for the create's own cleanup: by the time it resolves the
    // selected locker/tenant may already be something else.
    const createLockerId = lockerId
    const createTenantId = tenantId

    const f = async () => {
      // Switching directly to a different door (or locker) while the drawer is
      // still open — cancel the abandoned session on the door we're leaving,
      // same as clicking the close button, instead of leaving it to linger
      // until the scheduled fnCheckReservationSessions cleanup. Use the
      // LEAVING door's own locker/tenant — the currently selected locker may
      // have changed too if the switch also crossed lockers. Delete and create
      // are independent: a failed cleanup must not block the new door from
      // getting its own session.
      // (When doorId is undefined the drawer is closing instead — onClose
      // already removed that session, so skip it here.)
      if (previous.sessionId && doorId) {
        try {
          await onRemoveReservationSession(
            previous.sessionId,
            previous.lockerId,
            previous.tenantId,
          )
        } catch (e: any) {
          console.error(e)
        }
      }

      if (!canCreate) return

      try {
        const newSession = {
          thingTypeMrn: LockerDoorReservationSessionThingTypeMrn,
          tenantId: createTenantId,
          lockerId: createLockerId,
          sstLockerId: locker?.sstLockerId,
          sstDoorId: doorId,
          sessionStart: new Date().getTime(),
          userId: me.user?.id,
          tuId: me.tu?.id,
        }
        const res = await createThings({
          body: { things: [newSession] },
          params: { tenantId: createTenantId },
        }).unwrap()
        const createdSessionId = res?.things?.[0]?.id
        if (!createdSessionId) return

        if (
          previousDoor.current.doorId === doorId &&
          previousDoor.current.lockerId === createLockerId
        ) {
          // Precedence: a create we just made beats the polled `session`
          // selector, which may not have caught up for a brand-new session.
          previousDoor.current = {
            ...previousDoor.current,
            sessionId: createdSessionId,
          }
        } else {
          // We left this door while its create was in flight, so the row we
          // just wrote holds a door nobody is looking at. Release it now
          // rather than leaving it for the reaper.
          try {
            await onRemoveReservationSession(
              createdSessionId,
              createLockerId,
              createTenantId,
            )
          } catch (e: any) {
            console.error(e)
          }
        }
      } catch (e: any) {
        console.error(e)
      } finally {
        creatingDoorKeys.current.delete(doorKey)
      }
    }
    f()
  }, [
    createThings,
    door?.door_id,
    locker,
    lockerId,
    me.tu?.id,
    me.user?.id,
    onRemoveReservationSession,
    reservation?.id,
    session,
    session?.id,
    tenantId,
  ])

  // Module letter for the drawer title — matches the dashboard header.
  // V2 doors carry locker_group pointing at templates_association[locker_group];
  // visual position (A/B/C…) comes from `orderedV2Modules` — `visual_order`
  // then the locker's saved `moduleMapping`. It MUST be that same helper the
  // dashboard, the SA view and the deposit sheet use (LOCKER-390): a local
  // re-sort here is how the drawer title ends up naming a different module
  // than the tile the operator just clicked.
  const modName = useMemo(() => {
    if (locker?.sstApiVersion === "v2" && door?.door_id) {
      const v2Door = locker.sst2Locker?.doors?.find(
        (d: any) => d.id === door.door_id,
      )
      if (v2Door) {
        const visualIndex = orderedV2Modules(
          locker.sst2Locker,
          locker.moduleMapping,
        ).findIndex(({ originalIndex }) => originalIndex === v2Door.locker_group)
        if (visualIndex >= 0) {
          return `${t("module")} ${String.fromCharCode(65 + visualIndex)}`
        }
      }
    }
    return `${t("module")} 1`
  }, [locker, door?.door_id, t])

  const onClose = useCallback(
    async (sessionId?: string) => {
      // LOCKER-377 — the caller's id is a HINT, not the source of truth.
      // Escape arrives here through `useKeyPress`, which invokes its handler
      // with no arguments, so relying on the argument leaked the held door
      // every time. And once duplicate rows existed, the drawer only ever knew
      // about one of them. So close releases everything this manager can see
      // for the door: the id the caller passed, the id our own create returned,
      // and every session the store reports for the door.
      const held = previousDoor.current
      const toRemove = new Map<
        string,
        { lockerId?: string; tenantId?: string }
      >()
      if (sessionId) {
        toRemove.set(sessionId, { lockerId, tenantId })
      }
      if (held.sessionId) {
        toRemove.set(held.sessionId, {
          lockerId: held.lockerId,
          tenantId: held.tenantId,
        })
      }
      sessions?.forEach((s) => {
        if (!s?.id) return
        // SCOPE THE RELEASE TO THE DOOR WE ARE ACTUALLY LOOKING AT (LOCKER-377,
        // review F1.1). `sessions` is matched on `sstDoorId` alone — LOCKER-377
        // deliberately left that predicate alone, because whether SST door ids
        // are globally unique or unique only per locker is an open question the
        // ticket defers. Meanwhile the things cache accumulates across lockers
        // AND tenants: nothing dispatches `removeAll`, `setSelectedLockerId`
        // only sets the id, and `setTenantId` is dispatched at runtime. So an
        // unscoped release would delete another operator's live soft lock on a
        // different locker that happens to reuse the door id. Before the
        // release-all change that mis-attribution was inert — the delete was
        // keyed with the *selected* locker, so a foreign row got the wrong key
        // and missed. Keying each row correctly is what would make it land, so
        // the scope check has to come first.
        if (s.lockerId && s.lockerId !== lockerId) return
        if (s.tenantId && tenantId && s.tenantId !== tenantId) return
        // Within that scope, use the row's own locker/tenant for the delete key.
        toRemove.set(s.id, {
          lockerId: s.lockerId ?? lockerId,
          tenantId: s.tenantId ?? tenantId,
        })
      })

      // Both of these are synchronous and both happen BEFORE the awaits: the
      // ref reset stops the create effect from treating the closing door as a
      // new arrival, and the dispatch closes the drawer without making the
      // operator wait on the delete round trip.
      previousDoor.current = {
        doorId: undefined,
        sessionId: undefined,
        lockerId: undefined,
        tenantId: undefined,
      }
      dispatch(thingsActions.clearSelectedDoorReservation())

      for (const [id, owner] of toRemove) {
        try {
          await onRemoveReservationSession(id, owner.lockerId, owner.tenantId)
        } catch (e: any) {
          console.error(e)
        }
      }
    },
    [dispatch, lockerId, onRemoveReservationSession, sessions, tenantId],
  )

  // Release on unmount too (LOCKER-377 / D4). Navigating away from the
  // dashboard while a door is held used to leak the session to the reaper. The
  // remover is read through a ref so this cleanup can run exactly once, on
  // unmount, rather than on every change of its dependencies.
  const removeRef = useRef(onRemoveReservationSession)
  removeRef.current = onRemoveReservationSession
  useEffect(
    () => () => {
      const held = previousDoor.current
      if (!held.sessionId) return
      previousDoor.current = { ...held, sessionId: undefined }
      removeRef
        .current(held.sessionId, held.lockerId, held.tenantId)
        .catch((e: any) => console.error(e))
    },
    [],
  )

  return (
    <Box>
      {locker?.id && door?.door_id && (
        <OsLockerDoorDrawer
          modName={modName}
          onClose={onClose}
          door={door}
          reservation={reservation}
          locker={locker}
          session={session}
          isCreateMode={!reservation?.id}
        />
      )}
    </Box>
  )
}

export default OsLockerDoorDrawerManager
